import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { List, LayoutGrid, ArrowLeft, Bookmark } from "lucide-react";
import { CandidateCard, CandidatePanel } from "./CandidateComponents";
import AddToSequenceModal from "./sequencing/AddToSequenceModal.jsx";
import PreviewModal from "./sequencing/PreviewModal.jsx";
import Pagination from "../../components/shared_components/Pagination";
import { postAnalyticsEventService } from "../../Services/analyticsEvents.service";
import useServerPagination from "../hooks/useServerPagination";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

const SavedCandidates = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchId = location.state?.search_id || null;
    const jobId = location.state?.job_id || null;
    // Carried over by whoever linked here; what {{job_role}} resolves to when enrolling.
    const jobRole = location.state?.job_role || null;
    const searchName = location.state?.searchName || null;
    const orgId = useSelector(state => state.userSlice?.org_id) || localStorage.getItem("current_org_id");
    const recruiterName = useSelector(state => state.userSlice?.name) || localStorage.getItem("username") || "";
    const recruiterId = localStorage.getItem("org_admin_id");

    const [candidates, setCandidates] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState("list");
    const [shortlistedMap, setShortlistedMap] = useState({});
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [addToSeqCandidates, setAddToSeqCandidates] = useState(null);
    const [previewState, setPreviewState] = useState(null);
    const [enrollResult, setEnrollResult] = useState(null);
    const [bulkSeqCandidates, setBulkSeqCandidates] = useState(null);

    // Take the row from the list, not the object the panel was opened with, so an email
    // added in the panel is on it — the enroll gate reads these fields to decide readiness.
    const startEnroll = (c) => {
        const row = candidates.find(r => r.candidate_id === c.candidate_id);
        setAddToSeqCandidates([row || c]);
    };

    // A contact value revealed or added in the panel belongs on the row too, so the
    // enroll gate and the card behind the panel both see it without a refetch.
    const patchCandidateContact = (candidateId, changes) => {
        setCandidates(prev => prev.map(c =>
            c.candidate_id === candidateId ? { ...c, ...changes } : c
        ));
        setSelectedCandidate(prev =>
            prev?.candidate_id === candidateId ? { ...prev, ...changes } : prev
        );
    };

    // Server-side pagination: the API returns one page plus the org-wide total.
    const {
        currentPage,
        itemsPerPage,
        handlePageChange,
        handleItemsPerPageChange,
    } = useServerPagination({ totalItems: total });

    const fetchSaved = useCallback(async () => {
        if (!recruiterId) return;
        setLoading(true);
        setError(null);
        try {
            const params = {
                recruiter_id: recruiterId,
                organisation_id: orgId,
                page: currentPage,
                limit: itemsPerPage,
            };
            if (searchId) params.search_id = searchId;
            else if (jobId) params.job_id = jobId;
            const { data } = await axios.get(`${SOURCING_URL}/sourcing/saved`, { params });
            const list = data.candidates ?? [];
            setCandidates(list);
            setTotal(data.total ?? list.length);
            setShortlistedMap(Object.fromEntries(list.map(c => [c.candidate_id, c.is_shortlisted ?? false])));
        } catch {
            setError("Failed to load saved candidates. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [recruiterId, orgId, searchId, jobId, currentPage, itemsPerPage]);

    // Refetch whenever the page or page size changes — the server does the slicing.
    useEffect(() => { fetchSaved(); }, [fetchSaved]);

    const handleFindSimilar = (candidate) => {
        const yearsExp = candidate.years_of_experience;
        const location = candidate.location_city || candidate.location_state || candidate.location_country || null;
        const skills = candidate.skills || [];

        const builtQuery = {
            roles: [candidate.current_title].filter(Boolean),
            skills_required: skills.slice(0, 5),
            skills_nice_to_have: skills.slice(5, 10),
            locations: location ? [location] : [],
            seniority: candidate.seniority || null,
            experience_min: yearsExp != null ? Math.max(0, yearsExp - 3) : null,
            experience_max: yearsExp != null ? yearsExp + 3 : null,
            companies: [],
            limit: 10,
        };

        const displayQuery = `Similar profiles to ${candidate.full_name}${candidate.current_title ? ` – ${candidate.current_title}` : ""}`;

        setSelectedCandidate(null);
        navigate("/org-admin/sourcing/results", {
            state: {
                query: displayQuery,
                canonical_query: builtQuery,
                source_candidate: { name: candidate.full_name, title: candidate.current_title },
            },
        });
    };

    const handleUnsave = async (candidate) => {
        try {
            await axios.delete(`${SOURCING_URL}/sourcing/saved/${candidate.candidate_id}`, {
                params: { recruiter_id: recruiterId },
            });
            setCandidates(prev => prev.filter(c => c.candidate_id !== candidate.candidate_id));
            if (selectedCandidate?.candidate_id === candidate.candidate_id) {
                setSelectedCandidate(null);
            }
            // Only this page is in memory — refetch so the freed row is backfilled
            // from the next page and the total stays honest.
            fetchSaved();
        } catch {
            // silently ignore
        }
    };

    const handleShortlistToggle = async (candidate) => {
        const currentOrgId = orgId || localStorage.getItem("current_org_id");
        const adminId = recruiterId;
        const isShortlisted = shortlistedMap[candidate.candidate_id];
        try {
            if (isShortlisted) {
                await axios.delete(`${SOURCING_URL}/sourcing/shortlist/${candidate.candidate_id}`, {
                    params: { organisation_id: currentOrgId, search_id: searchId },
                });
                setShortlistedMap(prev => ({ ...prev, [candidate.candidate_id]: false }));
            } else {
                const { data: added } = await axios.post(`${SOURCING_URL}/sourcing/shortlist`, {
                    candidate_id: candidate.candidate_id,
                    organisation_id: currentOrgId,
                    recruiter_id: adminId,
                    recruiter_name: recruiterName,
                    search_id: searchId,
                });
                setShortlistedMap(prev => ({ ...prev, [candidate.candidate_id]: true }));
                // The Sourcing API answers with the job the search belongs to
                // and its shortlist size
                if (!added?.already) {
                    postAnalyticsEventService("shortlist_saved", {
                        search_id: searchId || undefined,
                        role_id: added?.job_id || undefined,
                        shortlist_size: added?.shortlist_size ?? undefined,
                    });
                }
                // remove from saved list since shortlist auto-unsaves server-side
                setCandidates(prev => prev.filter(c => c.candidate_id !== candidate.candidate_id));
                if (selectedCandidate?.candidate_id === candidate.candidate_id) {
                    setSelectedCandidate(null);
                }
                fetchSaved();   // backfill this page from the server
            }
        } catch (err) {
            if (err.response?.status === 409) setShortlistedMap(prev => ({ ...prev, [candidate.candidate_id]: true }));
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-white/[0.03]">

            {/* top bar */}
            <div className="bg-white dark:bg-white/5 border-b border-gray-100 dark:border-gray-700/50 px-6 py-4 flex items-center gap-4">
                <button
                    onClick={() => navigate(-1)}
                    className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-all"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2">
                   <p className="font-sans font-normal text-[20px] leading-[14px] tracking-[0] dark:text-[#FFFFFF]/90 text-textPrimary py-2">Saved {searchName ? ` · ${searchName}` : ""}</p>
                </div>
            </div>

            {/* content */}
            <div className="w-full px-6 py-6">

                {loading && (
                    <div className="flex flex-col items-center justify-center py-24 gap-3">
                        <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-gray-400 dark:text-gray-500">Loading saved candidates...</p>
                    </div>
                )}

                {error && !loading && (
                    <div className="text-center py-24">
                        <p className="text-sm text-red-400">{error}</p>
                        <button
                            onClick={fetchSaved}
                            className="mt-4 px-4 py-2 rounded-full text-sm text-purple-600 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30 hover:bg-purple-50"
                        >
                            Try again
                        </button>
                    </div>
                )}

                {!loading && !error && (
                    <>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <h2 className="text-base font-semibold text-gray-800 dark:text-white">
                                    Saved ({total})
                                </h2>
                                <div className="flex items-center gap-1 border border-gray-200 dark:border-gray-600 rounded-lg p-0.5">
                                    <button
                                        onClick={() => setViewMode("list")}
                                        className={`p-1.5 rounded-md transition-all ${
                                            viewMode === "list"
                                                ? "bg-purple-100 text-purple-600"
                                                : "text-gray-400 hover:text-gray-600"
                                        }`}
                                    >
                                        <List className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => setViewMode("grid")}
                                        className={`p-1.5 rounded-md transition-all ${
                                            viewMode === "grid"
                                                ? "bg-purple-100 text-purple-600"
                                                : "text-gray-400 hover:text-gray-600"
                                        }`}
                                    >
                                        <LayoutGrid className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {total === 0 ? (
                            <div className="text-center py-24">
                                <Bookmark className="w-10 h-10 text-gray-200 dark:text-gray-600 mx-auto mb-3" />
                                <p className="text-gray-400 dark:text-gray-500">No saved candidates yet.</p>
                                <p className="text-xs text-gray-300 dark:text-gray-600 mt-1">Save candidates from search results to review later.</p>
                            </div>
                        ) : (
                            <>
                            <div className={`bg-white rounded-2xl border border-gray-100 overflow-hidden ${
                                viewMode === "grid" ? "grid grid-cols-2 gap-px bg-gray-100" : ""
                            }`}>
                                {candidates.map((candidate) => (
                                    <CandidateCard
                                        key={candidate.candidate_id}
                                        candidate={candidate}
                                        onClick={() => setSelectedCandidate(candidate)}
                                        renderAction={(candidate) => (
                                            <div className="flex items-center gap-2 shrink-0">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); startEnroll(candidate); }}
                                                    className="flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium text-white transition-all"
                                                    style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                                                >
                                                    + Enroll
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleUnsave(candidate); }}
                                                    className="p-1.5 rounded-full text-gray-300 dark:text-gray-600 hover:text-red-400 hover:bg-red-50 transition-all"
                                                    title="Remove from saved"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}
                                    />
                                ))}
                            </div>
                            <div className="pt-6">
                                <Pagination
                                    currentPage={currentPage}
                                    totalItems={total}
                                    itemsPerPage={itemsPerPage}
                                    onPageChange={handlePageChange}
                                    onItemsPerPageChange={handleItemsPerPageChange}
                                    label="Candidates"
                                />
                            </div>
                            </>
                        )}
                    </>
                )}
            </div>

            {selectedCandidate && (
                <CandidatePanel
                    candidate={selectedCandidate}
                    onClose={() => setSelectedCandidate(null)}
                    shortlisted={shortlistedMap[selectedCandidate.candidate_id] ?? false}
                    onShortlist={handleShortlistToggle}
                    onEnroll={startEnroll}
                    onContactUpdate={patchCandidateContact}
                    onFindSimilar={handleFindSimilar}
                    jobId={jobId}
                    // Scheduling moves the candidate to Interview Management, so the saved
                    // list no longer holds them — refetch to backfill the page.
                    onScheduled={fetchSaved}
                />
            )}

            {addToSeqCandidates && (
                <AddToSequenceModal
                    candidates={addToSeqCandidates}
                    jobId={jobId}
                    jobRole={jobRole}
                    onClose={() => setAddToSeqCandidates(null)}
                    onPreview={(seq, list) => {
                        setPreviewState({ candidates: list, sequence: seq });
                        setAddToSeqCandidates(null);
                    }}
                    onEnrolled={(summary) => {
                        setAddToSeqCandidates(null);
                        setEnrollResult(summary);
                        setTimeout(() => setEnrollResult(null), 6000);
                    }}
                />
            )}

            {previewState && (
                <PreviewModal
                    candidates={previewState.candidates}
                    sequence={previewState.sequence}
                    jobId={jobId}
                    jobRole={jobRole}
                    onClose={() => setPreviewState(null)}
                    onBack={() => { setAddToSeqCandidates(previewState.candidates); setPreviewState(null); }}
                    onEnroll={(summary) => {
                        setPreviewState(null);
                        setEnrollResult(summary);
                        setTimeout(() => setEnrollResult(null), 6000);
                    }}
                />
            )}

            {enrollResult && (
                <div className={`fixed top-5 right-5 z-[100] flex items-start gap-2 px-4 py-3 bg-white rounded-xl shadow-lg border text-sm max-w-sm ${
                    enrollResult.tone === "error" ? "border-red-200 text-red-700"
                        : enrollResult.tone === "warn" ? "border-amber-200 text-amber-700"
                        : "border-green-200 text-green-700"
                }`}>
                    <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {enrollResult.tone === "success"
                            ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />}
                    </svg>
                    {enrollResult.message}
                </div>
            )}
        </div>
    );
};

export default SavedCandidates;
